import * as api from '../api';
import { isSubscriptionEnded, isSubscriptionInForce } from '../utils/subscriptions';

const initialState = Object.freeze({
    isFetching: false,
    error: null,

    id: null,
    subscription: null,
    trucks: [],
    trailers: [],
    clients: [],
    workOrders: [],
    materials: [],
    sites: [],
    drivers: [],
    ticketCounter: null,
    timeRoundingEnabled: false,
    timeRoundingInterval: null,
    ticketLimitStatus: {
        isAllowed: false,
        usage: 0,
        limit: null,
    },
    tagConfigs: [],
    dispatchNotificationChannels: [],
    timeZone: null,
    isEnterprise: false,
    isFree: false,
});

const state = {
    ...initialState
};

const getters = {
    subscriptionSuspended(state) {
        // A Free company is never suspended: when a paid upgrade lapses it falls back to
        // the Free plan. Its subscription history used to lock the owner out here.
        if (state.isFree) return false;

        return state.subscription && new Date(state.subscription.graceEndsAt) < new Date();
    },

    activeDrivers(state) {
        return state.drivers.filter(driver => driver.isActive);
    },

    timeZone(state) {
        return state.timeZone;
    },

    // HACK: Check if the company is Clark LLC to handle formatting differently
    // on reports to change Tons to Cubic Yards. This should be handled in the
    // backend, but this is a quick fix for now until we learn more about the
    // product need for this feature.
    isClarkLlc(state) {
        return state.id === 115;
    },

    // On Free and not paying for an upgrade. A canceled or lapsed subscription left over
    // from an earlier upgrade, or from before an admin moved the company to Free, does
    // not count.
    isFreePlan(state) {
        return state.isFree && !isSubscriptionInForce(state.subscription);
    },

    // Whether billing offers a Free company a fresh upgrade. Separate from isFreePlan,
    // which is about access: a lapsed upgrade that can still be paid puts the company
    // back on Free, but billing must keep its payment and retry controls, since the API
    // refuses a new subscription until the open one is paid or ends.
    canStartFreePlanUpgrade(state) {
        return state.isFree && (!state.subscription || isSubscriptionEnded(state.subscription));
    },

    isEnterpriseOrAdmin(state, getters, rootState) {
        return state.isEnterprise || rootState.auth.user?.role === 'admin';
    }
};

const mutations = {
    FETCH_PENDING(state) {
        state.isFetching = true;
    },

    FETCH_SUCCESS(state, overview) {
        state.id = overview.id;
        state.subscription = overview.subscription;
        state.trucks = overview.vehicles.filter(vehicle => vehicle.type === 'truck');
        state.trailers = overview.vehicles.filter(vehicle => vehicle.type === 'trailer');
        state.clients = overview.clients;
        state.workOrders = overview.workOrders;
        state.materials = overview.materials;
        state.sites = overview.sites;
        state.drivers = overview.drivers;
        state.ticketCounter = overview.ticketCounter;
        state.timeRoundingEnabled = !!overview.timeRoundingInterval;
        state.timeRoundingInterval = overview.timeRoundingInterval;
        state.tagConfigs = overview.tagConfigs || [];
        state.dispatchNotificationChannels = overview.dispatchNotificationChannels || [];
        state.timeZone = overview.timeZone;
        state.isEnterprise = overview.isEnterprise;
        state.isFree = overview.isFree;
        state.ticketLimitStatus = {
            isAllowed: overview.ticketLimitStatus?.isAllowed || false,
            usage: overview.ticketLimitStatus?.usage || 0,
            limit: overview.ticketLimitStatus?.limit || null,
        };

        state.isFetching = false;
        state.error = null;
    },

    FETCH_FAILURE(state, error) {
        state.isFetching = true;
        state.error = error.message;
    },

    CLEAR_STATE(state) {
        Object.assign(state, { ...initialState });
    },
};

const actions = {
    async fetchOverview({ rootGetters, commit }) {
        const companyId = rootGetters['auth/company'].id;

        commit('FETCH_PENDING');
        const res = await api.getCompanyOverview(companyId);
        if (res.error) {
            return commit('FETCH_FAILURE', res.error);
        }

        commit('FETCH_SUCCESS', res.body);
    },

    clearState({ commit }) {
        commit('CLEAR_STATE');
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
